import { create } from "zustand";
import axios, { type CancelTokenSource } from "axios";
import api, { isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface RouteCoordinate {
  latitud: number;
  longitud: number;
}

export type RouteAxisSource =
  | "google"
  | "mapbox"
  | "straight"
  | "fallback"
  | "invalid";

export const ROAD_FOLLOWING_AXIS_SOURCES: RouteAxisSource[] = ["google", "mapbox"];

export interface DenseRouteCoordinate {
  latitude: number;
  longitude: number;
}

export interface Route {
  id: string;
  name: string;
  description: string;
  status_id: string;
  status: { id: string; name: string };
  geofences_id: string[];
  principal_route: RouteCoordinate[];
  principal_route_dense?: DenseRouteCoordinate[];
  principal_route_source?: RouteAxisSource;
  principal_route_buffer?: number;
  principal_route_distance: number;
  principal_route_duration?: number;
  first_point_radius: number;
  last_point_radius: number;
  deviationAlertsEnabled?: boolean;
  company_id: string;
  vehicles_count: number;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
}

export interface CreateRouteData {
  name: string;
  description?: string;
  status_id?: string;
  geofences_id: string[];
  principal_route: RouteCoordinate[];
  principal_route_dense?: DenseRouteCoordinate[];
  principal_route_buffer?: number;
  first_point_radius: number;
  last_point_radius: number;
  deviationAlertsEnabled?: boolean;
  company_id: string;
}

export interface UpdateRouteData {
  name?: string;
  description?: string;
  status_id?: string;
  geofences_id?: string[];
  principal_route?: RouteCoordinate[];
  principal_route_dense?: DenseRouteCoordinate[];
  principal_route_buffer?: number;
  first_point_radius?: number;
  last_point_radius?: number;
  deviationAlertsEnabled?: boolean;
  company_id?: string;
}

interface RoutesStore {
  routes: Route[];
  loading: boolean;
  error: string | null;
  cancelToken: CancelTokenSource | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchRoutes: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    dateFrom?: string;
    dateTo?: string;
    filterBy?: "createdAt" | "updatedAt";
  }) => Promise<void>;
  fetchRoute: (routeId: string) => Promise<{ success: boolean; data?: Route; error?: string }>;
  createRoute: (routeData: CreateRouteData) => Promise<{ success: boolean; data?: Route; error?: string }>;
  updateRoute: (routeId: string, routeData: UpdateRouteData) => Promise<{ success: boolean; data?: Route; error?: string }>;
  deleteRoute: (routeId: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

export const useRoutesStore = create<RoutesStore>((set, get) => ({
  routes: [],
  loading: false,
  error: null,
  meta: undefined,
  cancelToken: null,

  fetchRoutes: async (params = {}) => {
    // Cancel any in-flight request: when the user changes filters quickly we
    // don't want a stale response to clobber the latest one.
    const { cancelToken } = get();
    if (cancelToken) {
      cancelToken.cancel("Superseded by newer fetchRoutes call");
    }
    const fetchGen = getAdminFetchGeneration();
    const newCancelToken = axios.CancelToken.source();
    set({ loading: true, error: null, cancelToken: newCancelToken });

    try {
      const limit = params.limit ?? 20;
      const page = params.page ?? 1;
      const { data } = await api.get("/rutasList", {
        params: {
          limit,
          page,
          // The previous default `offset: 1` was a bug — the controller is
          // page-based so it ignored offset, but if a backend ever honored it
          // we'd be skipping the first row. Derive from page when not given.
          offset: params.offset ?? (page - 1) * limit,
          ...params,
        },
        cancelToken: newCancelToken.token,
      });
      if (getAdminFetchGeneration() !== fetchGen) {
        set({ loading: false, cancelToken: null });
        return;
      }
      set({
        routes: data.data || [],
        meta: data.meta,
        loading: false,
        cancelToken: null,
      });
    } catch (err: any) {
      if (
        axios.isCancel(err) ||
        isAbortError(err) ||
        getAdminFetchGeneration() !== fetchGen
      ) {
        set({ loading: false, cancelToken: null });
        return;
      }
      console.error("Error fetching routes:", err);
      set({ error: getError(err), loading: false, cancelToken: null });
    }
  },

  fetchRoute: async (routeId: string) => {
    try {
      const { data } = await api.get("/rutas", {
        params: { ruta_id: routeId },
      });
      return { success: true, data: data.data.data };
    } catch (err: any) {
      console.error("Error fetching route:", err);
      return { success: false, error: getError(err) };
    }
  },

  createRoute: async (routeData: CreateRouteData) => {
    try {
      const { data } = await api.post("/rutas", routeData);

      const newRoute = data.data;
      set((state) => ({
        routes: [...state.routes, newRoute],
      }));

      return { success: true, data: newRoute };
    } catch (err: any) {
      console.error("Error creating route:", err);
      return { success: false, error: getError(err) };
    }
  },

  updateRoute: async (routeId: string, routeData: UpdateRouteData) => {
    try {
      const { data } = await api.put("/rutas", {
        ruta_id: routeId,
        ...routeData,
      });

      const updatedRoute = data.data;
      set((state) => ({
        routes: state.routes.map((route) =>
          route.id === routeId ? updatedRoute : route
        ),
      }));

      return { success: true, data: updatedRoute };
    } catch (err: any) {
      console.error("Error updating route:", err);
      return { success: false, error: getError(err) };
    }
  },

  deleteRoute: async (routeId: string) => {
    try {
      await api.delete("/rutas", {
        data: { ruta_id: routeId },
      });

      set((state) => ({
        routes: state.routes.filter((route) => route.id !== routeId),
      }));

      return { success: true };
    } catch (err: any) {
      console.error("Error deleting route:", err);
      return { success: false, error: getError(err) };
    }
  },

  clearError: () => set({ error: null }),
}));